import { getApiErrorMessage, IApiAxiosError } from "@/api";
import { IClientMinimal } from "@/api/clients/clients.types";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/**
 * Edit dialog for a conversation's title and client tag. Purely
 * presentational; the parent owns `onSave` and decides which mutations
 * to fire (assistant-ui rename for the title, direct PUT + cache
 * invalidate for the client).
 */
export interface EditConversationUpdates {
    title?: string;
    client_id?: number | null;
}

export interface EditConversationDialogProps {
    open: boolean;
    onClose: () => void;
    currentTitle: string;
    currentClient: IClientMinimal | null;
    onSave: (updates: EditConversationUpdates) => Promise<void>;
}

export const EditConversationDialog = ({
    open,
    onClose,
    currentTitle,
    currentClient,
    onSave,
}: EditConversationDialogProps) => {
    const [name, setName] = useState(currentTitle);
    const [client, setClient] = useState<IClientMinimal | null>(currentClient);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Prefill on open and when the identifying props change between opens;
    // intentionally not depending on `currentClient` reference identity so
    // unrelated parent re-renders don't reset in-progress edits.
    useEffect(() => {
        if (open) {
            setName(currentTitle);
            setClient(currentClient);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentTitle, currentClient?.id ?? null]);

    const handleSubmit = async () => {
        if (isSubmitting) return;
        const trimmedTitle = name.trim();
        if (!trimmedTitle) {
            setError("Name cannot be empty");
            return;
        }

        const updates: EditConversationUpdates = {};
        if (trimmedTitle !== currentTitle) {
            updates.title = trimmedTitle;
        }
        const nextClientId = client?.id ?? null;
        const currentClientId = currentClient?.id ?? null;
        if (nextClientId !== currentClientId) {
            updates.client_id = nextClientId;
        }

        // Nothing to save: close without firing onSave so the toast
        // doesn't lie about a change that didn't happen.
        if (Object.keys(updates).length === 0) {
            onClose();
            return;
        }

        setError(null);
        setIsSubmitting(true);
        try {
            await onSave(updates);
            toast.success("Conversation updated");
            onClose();
        } catch (err) {
            setError(
                getApiErrorMessage(
                    err as IApiAxiosError,
                    "Failed to update conversation"
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) handleClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Edit Conversation</DialogTitle>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleSubmit();
                    }}
                    className="flex flex-col gap-4"
                >
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="edit-conv-name">Name</Label>
                        <Input
                            id="edit-conv-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>
                    <ClientCombobox
                        id="edit-conv-client"
                        label="Client"
                        value={client}
                        onChange={setClient}
                        disabled={isSubmitting}
                    />
                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting || !name.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
